/**
 * Desktop Authentication Module
 * Handles authentication flow for Desktop Word environments with OIDC
 */

import { getCurrentUser } from "../utils/desktopAuthDialog";
import { getOidcAuthority, OIDC_CLIENT_ID } from "../config/environmentConstants";


/**
 * Validate token format (basic check for JWT structure)
 * @param {string} token - Token to validate
 * @returns {boolean} - Whether token appears to be valid format
 */
const validateTokenFormat = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // Check minimum length
  if (token.length < 20) {
    return false;
  }
  
  // Check for JWT format (3 parts separated by dots)
  const parts = token.split('.');
  if (parts.length === 3) {
    return true;
  } else {
    return true; // Still might be a valid non-JWT token
  }
};

/**
 * Extract access token from OIDC user data (Desktop/SharePoint)
 * @returns {string|null} - Access token or null if not found
 */
export const getDesktopAccessToken = () => {
  try {
    // First check OIDC client storage for proper tokens
    const oidcToken = getOidcClientToken();
    if (oidcToken && oidcToken.length > 100) {
      return oidcToken;
    }
    
    // Fallback to UserData (legacy/session tokens)
    const userData = getCurrentUser();
    
    if (userData && userData.access_token) {
      
      // Check if this is a proper OIDC token (should be longer than 100 chars typically)
      if (userData.access_token.length < 100) {
        console.warn("[OIDC-AUTH] ⚠️ UserData token appears to be session token, not OIDC token:", userData.access_token.length, "chars");
        console.warn("[OIDC-AUTH] ⚠️ This may cause 403 errors - recommend re-authentication");
      }
      
      // Validate token format
      if (!validateTokenFormat(userData.access_token)) {
        console.log("[OIDC-AUTH] ❌ Token format validation failed");
        return null;
      }
      
      return userData.access_token;
    }
    
    return null;
  } catch (error) {
    console.error("[OIDC-AUTH] Error getting access token:", error);
    return null;
  }
};

/**
 * Get access token from OIDC client storage (proper OIDC tokens)
 * @returns {string|null} - OIDC access token or null
 */
const getOidcClientToken = () => {
  try {
    // Check OIDC client storage for user data
    const oidcAuthority = getOidcAuthority();
    const clientId = OIDC_CLIENT_ID;
    const oidcUserKey = `oidc.user:${oidcAuthority}:${clientId}`;
    
    
    const oidcUserData = localStorage.getItem(oidcUserKey);
    if (oidcUserData) {
      const parsedOidcData = JSON.parse(oidcUserData);
      
      if (parsedOidcData.access_token && !parsedOidcData.expired) {
        return parsedOidcData.access_token;
      } else if (parsedOidcData.expired) {
      }
    } else {
    }
    
    return null;
  } catch (error) {
    console.error("[OIDC-AUTH] Error getting OIDC client token:", error);
    return null;
  }
};

/**
 * Test the current token by making a simple API call
 * This helps diagnose authentication issues
 * @returns {Promise<Object>} - Test result
 */
export const testCurrentToken = async () => {
  console.log("[OIDC-AUTH] 🧪 Testing current token...");
  
  try {
    const token = getDesktopAccessToken();
    if (!token) {
      return { success: false, error: "No token available" };
    }
    
    let baseUrl;
    try {
      baseUrl = getDesktopApiBaseUrl();
    } catch (error) {
      console.error("[OIDC-AUTH] ❌ Failed to get desktop API URL for token test:", error.message);
      return { success: false, error: `Configuration Error: ${error.message}. Please sign out and sign in again.` };
    }
    
    const testUrl = `${baseUrl}/api/LegalReferences?settings=false`;
    
    
    const response = await fetch(testUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    
    if (response.ok) {
      return { success: true, status: response.status };
    } else {
      const errorText = await response.text();
      return { success: false, status: response.status, error: errorText };
    }
    
  } catch (error) {
    console.error("[OIDC-AUTH] 🧪 ❌ Token test error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Extract subdomain from desktop user data for API base URL
 * @returns {string|null} - Subdomain or null if not found
 */
export const getDesktopSubdomain = () => {
  try {
    const userData = getCurrentUser();
    if (userData && userData.profile && userData.profile.subdomain) {
      return userData.profile.subdomain;
    }
    return null;
  } catch (error) {
    console.error("[OIDC-AUTH] Error getting subdomain:", error);
    return null;
  }
};

/**
 * Get API base URL using subdomain (Desktop/SharePoint)
 * @returns {string} - API base URL
 * @throws {Error} - If subdomain is not available or domain generation fails
 */
export const getDesktopApiBaseUrl = () => {
  const subdomain = getDesktopSubdomain();
  
  if (!subdomain) {
    const error = new Error("Unable to determine API base URL: No subdomain found in user profile. Please ensure you are properly authenticated.");
    error.code = 'NO_SUBDOMAIN';
    console.error("[OIDC-AUTH] ❌ No subdomain available:", error.message);
    throw error;
  }

  // Dynamically generate the base domain using same logic as OIDC authority
  try {
    const hostname = window.location.hostname;
    const domainParts = hostname.split('.');
    
    // Check if this is a local development environment
    const isLocalhost = hostname === 'localhost' || 
                       hostname === '127.0.0.1' || 
                       hostname.startsWith('192.168.') ||
                       hostname.startsWith('10.') ||
                       hostname.endsWith('.local') ||
                       hostname.includes('localhost');
    
    // Get community value from UserData (profile.aud)
    let communityValue = 'community'; // fallback
    try {
      const userData = getCurrentUser();
      if (userData && userData.profile && userData.profile.aud) {
        communityValue = userData.profile.aud;
        console.log("[OIDC-AUTH] ✅ Community value from profile.aud:", communityValue);
        
        // Check region_code for Desktop/SharePoint environments
        if (userData.profile.region_code) {
          const regionCode = userData.profile.region_code;
          console.log("[OIDC-AUTH] 🌍 Region code found:", regionCode);
          
          if (regionCode.toUpperCase() !== 'US') {
            // For non-US regions, append region code to aud
            communityValue = `${communityValue}-${regionCode.toLowerCase()}`;
            console.log("[OIDC-AUTH] 🌍 Non-US region detected, updated community value:", communityValue);
          } else {
            console.log("[OIDC-AUTH] 🇺🇸 US region detected, using aud as is:", communityValue);
          }
        } else {
          console.log("[OIDC-AUTH] ⚠️ No region_code found, using aud as is:", communityValue);
        }
      } else {
        console.warn("[OIDC-AUTH] ⚠️ No profile.aud found in UserData, using fallback 'community'");
      }
    } catch (error) {
      console.warn("[OIDC-AUTH] ⚠️ Error getting community value from UserData:", error.message);
    }

    if (isLocalhost) {
      // For local development, use a fallback API base URL with dynamic community value
      const fallbackApiUrl = `https://${subdomain}.${communityValue}.diligentoneplatform-dev.com`;
      console.log(`[OIDC-AUTH] 🏠 Local development environment detected: ${hostname}. Using fallback API URL:`, fallbackApiUrl);
      return fallbackApiUrl;
    }
    
    if (domainParts.length >= 3) {
      // Extract platform and extension using same logic as OIDC authority
      // Example: diligentcommunityaddon.diligentoneplatform-dev.com
      // -> platform: 'diligentoneplatform-dev', extension: 'com'
      const platformPart = domainParts[domainParts.length - 2];
      const extension = domainParts[domainParts.length - 1];
      const apiBaseUrl = `https://${subdomain}.${communityValue}.${platformPart}.${extension}`;
      console.log("[OIDC-AUTH] ✅ API base URL:", apiBaseUrl);
      return apiBaseUrl;
    } else {
      const error = new Error(`Unable to determine API base URL: Invalid hostname structure '${hostname}'. Expected format: subdomain.platform.extension`);
      error.code = 'INVALID_HOSTNAME';
      console.error("[OIDC-AUTH] ❌ Invalid hostname structure:", error.message);
      throw error;
    }
  } catch (error) {
    if (error.code) {
      // Re-throw our custom errors
      throw error;
    }
    // Handle unexpected errors
    const customError = new Error(`Unable to determine API base URL: ${error.message}`);
    customError.code = 'DOMAIN_GENERATION_ERROR';
    console.error("[OIDC-AUTH] ❌ Error generating dynamic domain:", customError.message);
    throw customError;
  }
};

/**
 * Check if access token is expired or about to expire
 * @returns {boolean} - True if token needs refresh
 */
export const isTokenExpired = () => {
  try {
    const userData = getCurrentUser();
    if (!userData || !userData.expires_at) {
      console.log("[DESKTOP-AUTH] No expiration data found");
      return true;
    }
    
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    const expiresAt = userData.expires_at;
    const bufferTime = 300; // 5 minutes buffer before expiration
    
    const isExpired = currentTime >= (expiresAt - bufferTime);
    console.log("[DESKTOP-AUTH] Token expiration check:");
    console.log("[DESKTOP-AUTH] - Current time:", currentTime);
    console.log("[DESKTOP-AUTH] - Expires at:", expiresAt);
    console.log("[DESKTOP-AUTH] - Is expired/expiring:", isExpired);
    
    return isExpired;
  } catch (error) {
    console.error("[DESKTOP-AUTH] Error checking token expiration:", error);
    return true;
  }
};

/**
 * Get refresh token from OIDC client storage or UserData
 * @returns {string|null} - Refresh token or null if not found
 */
export const getRefreshToken = () => {
  try {
    // First check OIDC client storage
    const oidcRefreshToken = getOidcClientRefreshToken();
    if (oidcRefreshToken) {
      console.log("[OIDC-AUTH] ✅ Using OIDC client refresh token");
      return oidcRefreshToken;
    }
    
    // Fallback to UserData
    const userData = getCurrentUser();
    if (userData && userData.refresh_token) {
      console.log("[OIDC-AUTH] ⚠️ Using UserData refresh token (fallback)");
      return userData.refresh_token;
    }
    
    console.log("[OIDC-AUTH] ❌ No refresh token found in OIDC storage or UserData");
    return null;
  } catch (error) {
    console.error("[OIDC-AUTH] Error getting refresh token:", error);
    return null;
  }
};

/**
 * Get refresh token from OIDC client storage
 * @returns {string|null} - OIDC refresh token or null
 */
const getOidcClientRefreshToken = () => {
  try {
    const oidcAuthority = getOidcAuthority();
    const clientId = OIDC_CLIENT_ID;
    const oidcUserKey = `oidc.user:${oidcAuthority}:${clientId}`;
    
    const oidcUserData = localStorage.getItem(oidcUserKey);
    if (oidcUserData) {
      const parsedOidcData = JSON.parse(oidcUserData);
      if (parsedOidcData.refresh_token && !parsedOidcData.expired) {
        console.log("[OIDC-AUTH] ✅ OIDC client refresh token found");
        return parsedOidcData.refresh_token;
      }
    }
    
    return null;
  } catch (error) {
    console.error("[OIDC-AUTH] Error getting OIDC client refresh token:", error);
    return null;
  }
};

/**
 * Update OIDC client storage with new token data after refresh
 * @param {Object} tokenData - New token data from refresh
 */
const updateOidcClientStorage = (tokenData) => {
  try {
    const oidcAuthority = getOidcAuthority();
    const clientId = OIDC_CLIENT_ID;
    const oidcUserKey = `oidc.user:${oidcAuthority}:${clientId}`;
    
    const existingOidcData = localStorage.getItem(oidcUserKey);
    if (existingOidcData) {
      const parsedOidcData = JSON.parse(existingOidcData);
      
      // Update OIDC client data with new tokens
      const updatedOidcData = {
        ...parsedOidcData,
        access_token: tokenData.access_token,
        expires_at: Math.floor(Date.now() / 1000) + (tokenData.expires_in || 3600),
        expired: false,
        ...(tokenData.refresh_token && { refresh_token: tokenData.refresh_token })
      };
      
      localStorage.setItem(oidcUserKey, JSON.stringify(updatedOidcData));
      console.log("[OIDC-AUTH] ✅ OIDC client storage updated with new tokens");
    } else {
      console.log("[OIDC-AUTH] ⚠️ No existing OIDC client data to update");
    }
  } catch (error) {
    console.error("[OIDC-AUTH] Error updating OIDC client storage:", error);
  }
};

/**
 * Refresh the access token using refresh token
 * @returns {Promise<boolean>} - True if refresh successful
 */
export const refreshAccessToken = async () => {
  console.log("[OIDC-AUTH] 🔄 Starting token refresh...");
  
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      console.log("[OIDC-AUTH] ❌ No refresh token available");
      return false;
    }
    

    const tokenEndpoint = `${getOidcAuthority()}/oauth/token`;
    console.log("[OIDC-AUTH] 🔍 Token refresh endpoint:", tokenEndpoint);
    console.log("[OIDC-AUTH] 🔍 Refresh token length:", refreshToken.length);
    
    const requestBody = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: OIDC_CLIENT_ID,
      refresh_token: refreshToken,
      scope: 'openid email profile membership'
    });
    
    console.log("[OIDC-AUTH] 🔍 Making refresh request...");

    
    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },

      body: requestBody

    });
    
    console.log("[OIDC-AUTH] 🔍 Refresh response status:", response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("[OIDC-AUTH] ❌ Token refresh failed:");
      console.error("[OIDC-AUTH] - Status:", response.status, response.statusText);
      console.error("[OIDC-AUTH] - Response:", errorText);
      console.error("[OIDC-AUTH] - Endpoint used:", tokenEndpoint);
      return false;
    }
    
    const tokenData = await response.json();
    console.log("[OIDC-AUTH] ✅ Token refresh successful");
    console.log("[OIDC-AUTH] 🔍 New token length:", tokenData.access_token?.length || 0);
    
    // Update both UserData and OIDC client storage with new tokens
    const currentUserData = getCurrentUser();
    const updatedUserData = {
      ...currentUserData,
      access_token: tokenData.access_token,
      expires_at: Math.floor(Date.now() / 1000) + (tokenData.expires_in || 3600),
      // Update refresh token if a new one is provided (rotation)
      ...(tokenData.refresh_token && { refresh_token: tokenData.refresh_token })
    };
    
    // Store updated user data in UserData
    localStorage.setItem("UserData", JSON.stringify(updatedUserData));
    console.log("[OIDC-AUTH] ✅ Updated UserData stored");
    
    // Also update OIDC client storage if it exists
    updateOidcClientStorage(tokenData);
    
    console.log("[OIDC-AUTH] New token expires at:", updatedUserData.expires_at);
    
    return true;
    
  } catch (error) {
    console.error("[DESKTOP-AUTH] ❌ Token refresh error:", error);
    return false;
  }
};

/**
 * Ensure valid access token (refresh if needed)
 * @returns {Promise<string|null>} - Valid access token or null if failed
 */
export const ensureValidToken = async () => {
  console.log("[OIDC-AUTH] 🔍 Ensuring valid access token...");
  
  try {
    // Debug: Log current user data state
    const userData = getCurrentUser();
    if (userData) {
      console.log("[OIDC-AUTH] 🔍 UserData Debug:");
      console.log("[OIDC-AUTH] - Access token present:", !!userData.access_token);
      console.log("[OIDC-AUTH] - Refresh token present:", !!userData.refresh_token);
      console.log("[OIDC-AUTH] - Expires at:", userData.expires_at ? new Date(userData.expires_at * 1000).toISOString() : "Not set");
      console.log("[OIDC-AUTH] - Current time:", new Date().toISOString());
    } else {
      console.log("[OIDC-AUTH] ❌ No UserData found in localStorage");
      return null;
    }
    
    // Check if current token is valid
    const isExpired = isTokenExpired();
    console.log("[OIDC-AUTH] 🔍 Token expiration check:", isExpired ? "EXPIRED" : "VALID");
    
    if (!isExpired) {
      const token = getDesktopAccessToken();
      console.log("[OIDC-AUTH] ✅ Current token is still valid");
      return token;
    }
    
    console.log("[OIDC-AUTH] Token expired or expiring, attempting refresh...");
    
    // Try to refresh the token
    const refreshSuccess = await refreshAccessToken();
    if (refreshSuccess) {
      const newToken = getDesktopAccessToken();
      console.log("[OIDC-AUTH] ✅ Token refreshed successfully");
      return newToken;
    } else {
      console.log("[OIDC-AUTH] ❌ Token refresh failed - user needs to re-authenticate");
      console.log("[OIDC-AUTH] This might be due to:");
      console.log("[OIDC-AUTH] - Invalid refresh token");
      console.log("[OIDC-AUTH] - OIDC authority URL mismatch");
      console.log("[OIDC-AUTH] - Session expired on server side");
      redirectToLogin();
      return null;
    }
    
  } catch (error) {
    console.error("[OIDC-AUTH] ❌ Error ensuring valid token:", error);
    console.log("[OIDC-AUTH] Redirecting to login due to token validation error");
    redirectToLogin();
    return null;
  }
};

/**
 * Handle Desktop authentication flow
 * @returns {Object} - Authentication result
 */
export const handleDesktopAuth = async () => {
  console.log("[DESKTOP-AUTH] Starting Desktop authentication flow...");
  
  try {
    console.log("[DESKTOP-AUTH] Checking Desktop authentication status...");
    
    // Check if user is already logged in from previous session
    const currentUser = getCurrentUser();
    
    if (currentUser) {
      console.log("[DESKTOP-AUTH] ✅ User found:", currentUser.profile?.name || currentUser.profile?.email);
      
      // Log current token status and refresh token availability
      console.log("[DESKTOP-AUTH] 🔍 Current UserData analysis:");
      console.log("[DESKTOP-AUTH] - Access token:", currentUser.access_token ? `Present (${currentUser.access_token.length} chars)` : "Missing");
      console.log("[DESKTOP-AUTH] - Refresh token:", currentUser.refresh_token ? `Present (${currentUser.refresh_token.length} chars)` : "❌ MISSING");
      console.log("[DESKTOP-AUTH] - Expires at:", currentUser.expires_at ? new Date(currentUser.expires_at * 1000).toLocaleString() : "No expiration data");
      console.log("[DESKTOP-AUTH] - Scope:", currentUser.scope || "No scope data");
      console.log("[DESKTOP-AUTH] - Token type:", currentUser.token_type || "No token type");
      
      // Check token expiration status
      const isExpired = isTokenExpired();
      console.log("[DESKTOP-AUTH] - Token expired/expiring:", isExpired ? "⚠️ YES" : "✅ NO");
      
      // Ensure we have a valid access token (refresh if needed)
      const validToken = await ensureValidToken();
      
      if (validToken) {
        console.log("[DESKTOP-AUTH] ✅ Valid access token confirmed - proceeding to PolicyReference");
        return {
          success: true,
          component: 'PolicyReference',
          message: 'Desktop authentication successful',
          user: getCurrentUser() // Get updated user data after potential refresh
        };
      } else {
        console.log("[DESKTOP-AUTH] ❌ Token refresh failed - user needs to re-authenticate");
        return {
          success: false,
          component: 'Login',
          message: 'Session expired - please sign in again',
          authType: 'desktop'
        };
      }
    } else {
      console.log("[DESKTOP-AUTH] No existing authentication found - showing Login component");
      
      // User needs to log in
      return {
        success: false,
        component: 'Login',
        message: 'Desktop authentication required',
        authType: 'desktop'
      };
    }
    
  } catch (error) {
    console.error("[DESKTOP-AUTH] ❌ Desktop authentication error:", error.message);
    return {
      success: false,
      component: 'ServiceUnavailable', 
      error: error.message,
      message: 'Desktop authentication service unavailable'
    };
  }
};

/**
 * Check if Desktop environment is ready for authentication
 * @returns {boolean} - Whether Desktop is ready
 */
export const isDesktopReady = () => {
  return window.officeReady === true;
};

/**
 * TEST FUNCTION: Force token expiration for testing refresh logic
 * This simulates an expired token by setting expires_at to past time
 */
export const forceTokenExpiration = () => {
  console.log("[DESKTOP-AUTH] 🧪 TEST: Forcing token expiration");
  try {
    const userData = getCurrentUser();
    if (userData) {
      const expiredUserData = {
        ...userData,
        expires_at: Math.floor(Date.now() / 1000) - 3600 // Set to 1 hour ago
      };
      localStorage.setItem("UserData", JSON.stringify(expiredUserData));
      console.log("[DESKTOP-AUTH] 🧪 TEST: Token forced to expired state");
      return true;
    }
    return false;
  } catch (error) {
    console.error("[DESKTOP-AUTH] 🧪 TEST: Error forcing expiration:", error);
    return false;
  }
};

// Make it available globally for testing
window.forceTokenExpiration = forceTokenExpiration;
window.redirectToLogin = redirectToLogin;

/**
 * Clear all authentication data and force fresh login
 * Use this when you need to test the complete OIDC flow with offline_access
 */
export const clearAuthenticationData = () => {
  console.log("[DESKTOP-AUTH] 🧹 Clearing all authentication data...");
  
  try {
    // Clear localStorage keys

    const keysToRemove = ['UserData', `oidc.user:${getOidcAuthority()}:${OIDC_CLIENT_ID}`];

    keysToRemove.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`[DESKTOP-AUTH] ✅ Cleared localStorage key: ${key}`);
      }
    });
    
    // Clear any sessionStorage keys
    const sessionKeys = ['isDoubleJump'];
    sessionKeys.forEach(key => {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
        console.log(`[DESKTOP-AUTH] ✅ Cleared sessionStorage key: ${key}`);
      }
    });
    
    console.log("[DESKTOP-AUTH] 🧹 Authentication data cleared successfully");
    console.log("[DESKTOP-AUTH] 🔄 Please refresh the page to start fresh authentication flow");
    
    return true;
  } catch (error) {
    console.error("[DESKTOP-AUTH] ❌ Error clearing authentication data:", error);
    return false;
  }
};

// Make it available globally for testing
window.clearAuthenticationData = clearAuthenticationData;

/**
 * Redirect to login page when token expires
 * Clears authentication data and reloads the page to show login
 */
export const redirectToLogin = () => {
  console.log("[DESKTOP-AUTH] 🔄 Redirecting to login due to token expiry...");
  
  try {
    // Clear all authentication data
    localStorage.removeItem("UserData");
    localStorage.setItem("IsUserLoggedIn", "false");
    
    // Clear any OIDC storage
    const oidcKey = `oidc.user:${getOidcAuthority()}:${OIDC_CLIENT_ID}`;
    if (localStorage.getItem(oidcKey)) {
      localStorage.removeItem(oidcKey);
    }
    
    console.log("[DESKTOP-AUTH] ✅ Authentication data cleared");
    
    // Reload the page to trigger the login flow
    console.log("[DESKTOP-AUTH] 🔄 Reloading page to show login screen...");
    window.location.reload();
    
  } catch (error) {
    console.error("[DESKTOP-AUTH] ❌ Error during login redirect:", error);
    // Force reload even if clearing data failed
    window.location.reload();
  }
};

/**
 * Desktop-specific initialization
 */
export const initializeDesktop = () => {
  console.log("[DESKTOP-AUTH] Initializing Desktop environment...");
  
  // Immediately log refresh token status
  logRefreshTokenStatus();
  
  // Start periodic token expiry check
  startTokenExpiryMonitor();
  
  // Add any Desktop-specific initialization here
};

/**
 * Start monitoring token expiry and redirect to login when needed
 */
export const startTokenExpiryMonitor = () => {
  console.log("[DESKTOP-AUTH] 🕐 Starting token expiry monitor...");
  
  // Check token expiry every 5 minutes
  const MONITOR_INTERVAL = 5 * 60 * 1000; // 5 minutes
  
  const checkTokenExpiry = () => {
    try {
      const userData = getCurrentUser();
      if (!userData) {
        console.log("[DESKTOP-AUTH] No user data found - stopping expiry monitor");
        return;
      }
      
      if (isTokenExpired()) {
        console.log("[DESKTOP-AUTH] ⚠️ Token expired detected by monitor - redirecting to login");
        redirectToLogin();
        return;
      }
      
      // Check if token expires in the next 10 minutes
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = userData.expires_at - currentTime;
      const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60);
      
      if (minutesUntilExpiry <= 10 && minutesUntilExpiry > 0) {
        console.log(`[DESKTOP-AUTH] ⚠️ Token expires in ${minutesUntilExpiry} minutes - will redirect soon`);
      }
      
    } catch (error) {
      console.error("[DESKTOP-AUTH] ❌ Error in token expiry monitor:", error);
    }
  };
  
  // Initial check
  checkTokenExpiry();
  
  // Set up periodic monitoring
  const monitorInterval = setInterval(checkTokenExpiry, MONITOR_INTERVAL);
  
  // Store interval ID for cleanup if needed
  window.desktopAuthMonitorInterval = monitorInterval;
  
  console.log("[DESKTOP-AUTH] ✅ Token expiry monitor started (checking every 5 minutes)");
};

/**
 * Log detailed refresh token status for debugging
 */
export const logRefreshTokenStatus = () => {
  console.log("[DESKTOP-AUTH] 🔍 REFRESH TOKEN STATUS CHECK:");
  
  try {
    const userData = getCurrentUser();
    
    if (!userData) {
      console.log("[DESKTOP-AUTH] ❌ No user data found in localStorage");
      return;
    }
    
    console.log("[DESKTOP-AUTH] 📊 Complete UserData object:");
    console.log("[DESKTOP-AUTH] Keys in UserData:", Object.keys(userData));
    
    // Check each important field
    const fields = ['access_token', 'refresh_token', 'id_token', 'expires_at', 'scope', 'token_type'];
    fields.forEach(field => {
      const value = userData[field];
      if (value) {
        if (typeof value === 'string' && value.length > 50) {
          console.log(`[DESKTOP-AUTH] - ${field}: Present (${value.length} chars) - ${value.substring(0, 20)}...`);
        } else {
          console.log(`[DESKTOP-AUTH] - ${field}:`, value);
        }
      } else {
        console.log(`[DESKTOP-AUTH] - ${field}: ❌ MISSING`);
      }
    });
    
    // Special check for refresh token
    if (userData.refresh_token) {
      console.log("[DESKTOP-AUTH] ✅ REFRESH TOKEN IS AVAILABLE!");
      console.log("[DESKTOP-AUTH] Refresh token length:", userData.refresh_token.length);
    } else {
      console.log("[DESKTOP-AUTH] ❌ REFRESH TOKEN IS MISSING!");
      console.log("[DESKTOP-AUTH] No refresh token found - user will need to re-authenticate when access token expires");
    }
    
    // Check expiration
    if (userData.expires_at) {
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = userData.expires_at - currentTime;
      const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60);
      
      console.log("[DESKTOP-AUTH] ⏰ Token expiration info:");
      console.log("[DESKTOP-AUTH] - Current time:", new Date().toLocaleString());
      console.log("[DESKTOP-AUTH] - Expires at:", new Date(userData.expires_at * 1000).toLocaleString());
      console.log("[DESKTOP-AUTH] - Minutes until expiry:", minutesUntilExpiry);
      console.log("[DESKTOP-AUTH] - Is expired:", timeUntilExpiry <= 0 ? "YES ⚠️" : "NO ✅");
    }
    
  } catch (error) {
    console.error("[DESKTOP-AUTH] ❌ Error checking refresh token status:", error);
  }
};

/**
 * Handle Desktop login success callback
 * @param {Object} loginResult - Result from login dialog
 */
export const handleDesktopLoginSuccess = (loginResult) => {
  console.log("[DESKTOP-AUTH] Desktop login successful:", loginResult);
  // Handle successful Desktop login
  // This might involve storing tokens, updating UI, etc.
};

export default {
  handleDesktopAuth,
  isDesktopReady,
  initializeDesktop,
  handleDesktopLoginSuccess
};

/**
 * Debug function to analyze the current UserData in localStorage
 */
export const debugUserData = () => {
  console.log("[OIDC-DEBUG] 🔍 === USERDATA ANALYSIS ===");
  
  const isLoggedIn = localStorage.getItem("IsUserLoggedIn");
  const userDataRaw = localStorage.getItem("UserData");
  
  console.log("[OIDC-DEBUG] IsUserLoggedIn:", isLoggedIn);
  console.log("[OIDC-DEBUG] UserData raw length:", userDataRaw?.length || 0);
  
  if (userDataRaw) {
    try {
      const userData = JSON.parse(userDataRaw);
      console.log("[OIDC-DEBUG] 📋 UserData structure:");
      console.log("[OIDC-DEBUG] Keys in UserData:", Object.keys(userData));
      
      // Check all token-related fields
      Object.entries(userData).forEach(([key, value]) => {
        if (typeof value === 'string' && (
          key.toLowerCase().includes('token') || 
          key.toLowerCase().includes('access') ||
          key.toLowerCase().includes('refresh') ||
          key.toLowerCase().includes('id_token')
        )) {
          console.log(`[OIDC-DEBUG] - ${key}: length=${value.length}, starts=${value.substring(0, 20)}...`);
        } else if (key === 'profile') {
          console.log(`[OIDC-DEBUG] - ${key}:`, value);
        } else {
          console.log(`[OIDC-DEBUG] - ${key}: ${typeof value} (${value?.toString().substring(0, 50)}...)`);
        }
      });
      
      // Check if this looks like proper OIDC user data
      const hasAccessToken = userData.access_token && userData.access_token.length > 50;
      const hasRefreshToken = userData.refresh_token && userData.refresh_token.length > 10;
      const hasProfile = userData.profile && typeof userData.profile === 'object';
      
      console.log("[OIDC-DEBUG] 🎯 OIDC Data Quality Check:");
      console.log("[OIDC-DEBUG] - Has proper access token:", hasAccessToken);
      console.log("[OIDC-DEBUG] - Has refresh token:", hasRefreshToken);
      console.log("[OIDC-DEBUG] - Has profile data:", hasProfile);
      
      if (!hasAccessToken) {
        console.warn("[OIDC-DEBUG] ⚠️ ACCESS TOKEN ISSUE DETECTED!");
        console.warn("[OIDC-DEBUG] ⚠️ Current access_token appears to be session token, not OIDC token");
      }
      
    } catch (error) {
      console.error("[OIDC-DEBUG] Error parsing UserData:", error);
    }
  } else {
    console.log("[OIDC-DEBUG] No UserData found in localStorage");
  }
  
  // Also check for OIDC client storage
  console.log("[OIDC-DEBUG] 🔍 Checking OIDC client storage:");
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('oidc.')) {
      const value = localStorage.getItem(key);
      console.log(`[OIDC-DEBUG] OIDC key: ${key} (length: ${value?.length})`);
      
      // Try to parse OIDC user data if this looks like a user key
      if (key.includes('user:') && value) {
        try {
          const oidcUserData = JSON.parse(value);
          console.log(`[OIDC-DEBUG] 📋 OIDC User Data in ${key}:`, {
            hasAccessToken: !!oidcUserData.access_token,
            accessTokenLength: oidcUserData.access_token?.length,
            hasRefreshToken: !!oidcUserData.refresh_token,
            expired: oidcUserData.expired,
            profile: oidcUserData.profile
          });
          
          // Check if OIDC storage has a different (better) token
          if (oidcUserData.access_token && oidcUserData.access_token.length > 100) {
            console.warn("[OIDC-DEBUG] 🎯 FOUND BETTER TOKEN IN OIDC STORAGE!");
            console.warn("[OIDC-DEBUG] OIDC token length:", oidcUserData.access_token.length);
            console.warn("[OIDC-DEBUG] UserData token length:", userData?.access_token?.length || 0);
            console.warn("[OIDC-DEBUG] Consider using OIDC storage token instead of UserData token");
          }
        } catch (e) {
          console.log(`[OIDC-DEBUG] Could not parse OIDC data for ${key}`);
        }
      }
    }
  });
};

// Make the test functions available in console for debugging
if (typeof window !== 'undefined') {
  window.testCurrentToken = testCurrentToken;
  window.debugUserData = debugUserData;
  console.log("[OIDC-AUTH] 🔧 Debug functions available:");
  console.log("[OIDC-AUTH] - window.testCurrentToken() - Test current token against API");
  console.log("[OIDC-AUTH] - window.debugUserData() - Analyze UserData structure");
}
